import React, { Fragment } from "react";
import { Link } from "react-router-dom";

import Header from "../../../components/Header/Header";
import PageTitle from "../../../components/pagetitle/PageTitle";
import Footer from "../../../components/footer/Footer";
import Scrollbar from "../../../components/scrollbar/scrollbar";

import "./EngineeringTeamExtension.css";

/* =========================================================
   IMAGES
   Folder:
   src/images/engineering-team-extension/
========================================================= */

import heroImage from "../../../images/engineering-team-extension/engineering-team-hero.png";
import chipImage from "../../../images/engineering-team-extension/engineering-extension-chip.png";
import collaborationImage from "../../../images/engineering-team-extension/collaborative-engineering.png";
import ctaImage from "../../../images/engineering-team-extension/engineering-partner-cta.png";

/* =========================================================
   TYPES
========================================================= */

type Item = {
  title: string;
  text: string;
};

/* =========================================================
   DATA
========================================================= */

const engagementScenarios: Item[] = [
  {
    title: "Rapid Engineering Scale-Up",
    text:
      "Quickly expand engineering capacity to meet evolving project demands without disrupting ongoing development.",
  },
  {
    title: "Specialized Semiconductor Expertise",
    text:
      "Access experienced professionals across key semiconductor engineering disciplines to address specific technical requirements.",
  },
  {
    title: "Project Acceleration",
    text:
      "Strengthen existing teams to improve execution capacity, maintain development momentum, and achieve critical milestones.",
  },
  {
    title: "Flexible Team Expansion",
    text:
      "Scale engineering resources up or down as project priorities evolve while maintaining continuity and delivery efficiency.",
  },
  {
    title: "Long-Term Engineering Partnership",
    text:
      "Build an extended engineering organization that works collaboratively with internal teams to support sustained product development initiatives.",
  },
];

const howWeWork: Item[] = [
  {
    title: "Understand Your Engineering Requirements",
    text:
      "We align on project objectives, technical scope, team structure, required competencies, and engagement expectations before deployment.",
  },
  {
    title: "Build the Right Engineering Team",
    text:
      "We assemble a team of experienced semiconductor professionals whose skills, experience, and domain expertise align with your project requirements.",
  },
  {
    title: "Integrate with Your Organization",
    text:
      "Our engineers work within your engineering environment, adopting your tools, workflows, communication practices, and governance processes to ensure seamless collaboration.",
  },
  {
    title: "Execute with Delivery Discipline",
    text:
      "Through structured planning, regular progress reviews, and transparent communication, we maintain execution quality, delivery predictability, and engineering continuity.",
  },
  {
    title: "Scale as Your Needs Evolve",
    text:
      "As project priorities change, the engagement can be expanded, refined, or restructured to support evolving engineering and business requirements.",
  },
];

const differentiators: Item[] = [
  {
    title: "Semiconductor-Focused Expertise",
    text:
      "Our teams bring deep domain knowledge across the semiconductor development lifecycle, enabling meaningful contributions from the outset of every engagement.",
  },
  {
    title: "Seamless Team Integration",
    text:
      "We work as a natural extension of your engineering organization, aligning with your tools, processes, governance, and delivery culture.",
  },
  {
    title: "Flexible Engagement Models",
    text:
      "Whether you need a small specialist team or a larger multidisciplinary engineering group, our engagement model adapts to your project requirements and evolving priorities.",
  },
  {
    title: "Disciplined Delivery Execution",
    text:
      "Structured planning, transparent communication, milestone-driven execution, and consistent engineering practices help maintain quality, predictability, and delivery confidence.",
  },
  {
    title: "Long-Term Partnership Mindset",
    text:
      "We focus on building enduring engineering relationships, supporting customers through evolving programs, changing priorities, and future product development initiatives.",
  },
  {
    title: "Commitment to Engineering Excellence",
    text:
      "Every engagement is driven by technical integrity, continuous collaboration, and a shared commitment to delivering successful engineering outcomes.",
  },
];

const principles = [
  "Seamless Team Integration",
  "Engineering-Led Collaboration",
  "Transparent Communication",
  "Structured Governance",
  "Flexible Scalability",
  "Delivery Accountability",
];

/* =========================================================
   ICONS
========================================================= */

const Arrow = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M5 12h13M13 7l5 5-5 5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Icon = ({ index = 0 }: { index?: number }) => {
  const type = index % 5;

  if (type === 0) {
    return (
      <svg viewBox="0 0 48 48" aria-hidden="true">
        <rect
          x="13"
          y="13"
          width="22"
          height="22"
          rx="3"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />

        <rect
          x="19"
          y="19"
          width="10"
          height="10"
          rx="1"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />

        <path
          d="
            M18 7v6
            M24 7v6
            M30 7v6
            M18 35v6
            M24 35v6
            M30 35v6
            M7 18h6
            M7 24h6
            M7 30h6
            M35 18h6
            M35 24h6
            M35 30h6
          "
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </svg>
    );
  }

  if (type === 1) {
    return (
      <svg viewBox="0 0 48 48" aria-hidden="true">
        <circle
          cx="24"
          cy="17"
          r="6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />

        <circle
          cx="12"
          cy="22"
          r="4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
        />

        <circle
          cx="36"
          cy="22"
          r="4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
        />

        <path
          d="
            M13 39c1-8 5-12 11-12s10 4 11 12
            M4 37c1-6 4-9 9-9
            M44 37c-1-6-4-9-9-9
          "
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
        />
      </svg>
    );
  }

  if (type === 2) {
    return (
      <svg viewBox="0 0 48 48" aria-hidden="true">
        <path
          d="M9 34l9-10 8 6 13-17"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        <path
          d="M32 13h7v7"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        />
      </svg>
    );
  }

  if (type === 3) {
    return (
      <svg viewBox="0 0 48 48" aria-hidden="true">
        <path
          d="M24 6l14 6v10c0 10-6 16-14 20C16 38 10 32 10 22V12z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />

        <path
          d="M18 24l4 4 8-9"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 48 48" aria-hidden="true">
      <circle
        cx="24"
        cy="24"
        r="7"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />

      <path
        d="
          M24 6v6
          M24 36v6
          M6 24h6
          M36 24h6
          M11 11l4 4
          M33 33l4 4
          M37 11l-4 4
          M15 33l-4 4
        "
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
};

/* =========================================================
   PAGE
========================================================= */

const EngineeringTeamExtension: React.FC = () => {
  return (
    <Fragment>
      <div className="engineering-team-extension inner-page">
        <div className="body_wrap o-clip">
          <Header />

          <main>
            {/* =================================================
                PAGE TITLE
            ================================================= */}

            <PageTitle
              pageTitle="Engineering Team Extension"
              pagesub="Engineering Team Extension"
            />

            {/* =================================================
                HERO
            ================================================= */}

            <section className="ete-hero">
              <div className="ete-shell ete-hero-layout">
                <div className="ete-copy ete-hero-copy">
                  <span className="ete-kicker">
                    ENGINEERING TEAM EXTENSION
                  </span>

                  <h1>
                    Engineering
                    <span>Team Extension</span>
                  </h1>

                  <h3>
                    Extend Your Engineering Capability.
                    <br />
                    Accelerate Your Product Development.
                  </h3>

                  <p>
                    Semiconductor development programs often demand specialized
                    expertise, rapid team expansion, and the flexibility to
                    respond to changing project priorities. Building these
                    capabilities internally can take time, affecting schedules
                    and increasing execution risk.
                  </p>

                  <p>
                    Engineering Team Extension enables organizations to
                    strengthen their engineering capacity with experienced
                    semiconductor professionals who integrate seamlessly into
                    existing teams and development workflows.
                  </p>

                  <Link className="ete-btn" to="/contact">
                    Discuss Your Engineering Requirements
                    <Arrow />
                  </Link>
                </div>

                <div className="ete-photo ete-photo--hero">
                  <img
                    src={heroImage}
                    alt="Semiconductor engineering team"
                  />

                  <div className="ete-photo-labels">
                    <span>PEOPLE</span>
                    <span>EXPERTISE</span>
                    <span>COLLABORATION</span>
                    <span>RESULTS</span>
                  </div>
                </div>
              </div>
            </section>

            {/* =================================================
                OVERVIEW
            ================================================= */}

            <section className="ete-section ete-overview">
              <div className="ete-shell ete-two-col">
                <div className="ete-copy">
                  <span className="ete-kicker">OVERVIEW</span>

                  <h2>
                    Engineering
                    <br />
                    Team Extension
                  </h2>

                  <p>
                    Engineering priorities can change rapidly throughout a
                    semiconductor development program. Whether responding to
                    aggressive project timelines, addressing specialized skill
                    requirements, supporting peak workloads, or accelerating
                    execution, organizations often need to strengthen their
                    engineering capability without the delays associated with
                    conventional hiring.
                  </p>

                  <p>
                    Digicomm Semiconductor enables customers to extend their
                    engineering organization with experienced semiconductor
                    professionals who work as an integrated part of existing
                    teams. Our collaborative engagement model ensures seamless
                    alignment with customer processes, engineering
                    methodologies, and project objectives.
                  </p>
                </div>

                <div className="ete-overview-visual">
                  <img
                    src={chipImage}
                    alt="Engineering capability visualization"
                  />

                  <div className="ete-capabilities">
                    {[
                      "FLEXIBLE",
                      "SCALABLE",
                      "SPECIALIZED",
                      "RELIABLE",
                    ].map((item, i) => (
                      <div className="ete-capability" key={item}>
                        <span>
                          <Icon index={i} />
                        </span>

                        <strong>{item}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* =================================================
                ENGAGEMENT SCENARIOS
            ================================================= */}

            <section className="ete-section ete-scenarios">
              <div className="ete-shell">
                <div className="ete-heading">
                  <span className="ete-kicker">
                    IDEAL ENGAGEMENT SCENARIOS
                  </span>

                  <h2>
                    When to Extend Your Engineering Team
                  </h2>
                </div>

                <div className="ete-scenario-grid">
                  {engagementScenarios.map((item, i) => (
                    <article key={item.title}>
                      <span className="ete-index">
                        {String(i + 1).padStart(2, "0")}
                      </span>

                      <div className="ete-card-icon">
                        <Icon index={i} />
                      </div>

                      <h3>{item.title}</h3>

                      <p>{item.text}</p>
                    </article>
                  ))}
                </div>
              </div>
            </section>

            {/* =================================================
                COLLABORATION
            ================================================= */}

            <section className="ete-section ete-collab">
              <div className="ete-shell ete-collab-top">
                <div className="ete-photo ete-photo--collab">
                  <img
                    src={collaborationImage}
                    alt="Collaborative semiconductor engineering team"
                  />

                  <div className="ete-collab-tag">
                    <span>INTEGRATED</span>
                    <span>ALIGNED</span>
                    <span>COLLABORATIVE</span>
                    <span>SUCCESSFUL</span>
                  </div>
                </div>

                <div className="ete-copy">
                  <span className="ete-kicker">
                    OUR APPROACH
                  </span>

                  <h2>
                    A Collaborative
                    <br />
                    Engineering Extension
                  </h2>

                  <p>
                    Successful engineering partnerships are built on more than
                    technical expertise—they depend on seamless collaboration,
                    clear communication, and shared accountability. Digicomm's
                    Engineering Team Extension model is designed to integrate
                    naturally with your engineering organization.
                  </p>

                  <p>
                    Our engineers work as an extension of your organization,
                    collaborating closely with internal stakeholders to maintain
                    project continuity, accelerate execution, and support
                    consistent engineering outcomes throughout the engagement.
                  </p>
                </div>
              </div>

              {/* =================================================
                  HOW WE WORK
              ================================================= */}

              <div className="ete-shell ete-process-block">
                <div className="ete-heading">
                  <span className="ete-kicker">
                    HOW WE WORK
                  </span>

                  <h2>
                    Built for Seamless Integration
                  </h2>
                </div>

                <div className="ete-process-list">
                  {howWeWork.map((item, i) => (
                    <article key={item.title}>
                      <span className="ete-process-no">
                        {String(i + 1).padStart(2, "0")}
                      </span>

                      <div>
                        <h3>{item.title}</h3>

                        <p>{item.text}</p>
                      </div>
                    </article>
                  ))}
                </div>

                <div className="ete-principles">
                  {principles.map((item, i) => (
                    <div key={item}>
                      <span>
                        {String(i + 1).padStart(2, "0")}
                      </span>

                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* =================================================
                WHY DIGICOMM
            ================================================= */}

            <section className="ete-section ete-why">
              <div className="ete-shell ete-why-layout">
                <div className="ete-copy">
                  <span className="ete-kicker">
                    WHY DIGICOMM
                  </span>

                  <h2>
                    Why Organizations
                    <br />
                    Choose Digicomm
                  </h2>

                  <p>
                    Choosing an engineering partner is about more than filling
                    capability gaps. It is about finding a team that understands
                    the complexity of semiconductor product development,
                    integrates seamlessly with your organization, and
                    consistently delivers against technical and business
                    objectives.
                  </p>

                  <Link className="ete-btn" to="/contact">
                    Partner with Our Engineering Teams
                    <Arrow />
                  </Link>
                </div>

                <div className="ete-value-grid">
                  {differentiators.map((item, i) => (
                    <article key={item.title}>
                      <div className="ete-card-icon">
                        <Icon index={i} />
                      </div>

                      <h3>{item.title}</h3>

                      <p>{item.text}</p>
                    </article>
                  ))}
                </div>
              </div>
            </section>

            {/* =================================================
                FINAL CTA
            ================================================= */}

            <section className="ete-final-cta">
              <div className="ete-shell ete-cta-layout">
                <div className="ete-copy">
                  <span className="ete-kicker">
                    LET'S BUILD TOGETHER
                  </span>

                  <h2>
                    Strengthen Your Engineering
                    <br />
                    Capability with Confidence
                  </h2>

                  <p>
                    Whether you're expanding an existing engineering team,
                    addressing specialized skill requirements, or accelerating
                    a critical semiconductor development program, Digicomm
                    Semiconductor provides experienced engineering
                    professionals who integrate seamlessly with your
                    organization.
                  </p>

                  <Link className="ete-btn" to="/contact">
                    Discuss Your Engineering Requirements
                    <Arrow />
                  </Link>
                </div>

                <div className="ete-photo ete-photo--cta">
                  <img
                    src={ctaImage}
                    alt="Engineering partnership"
                  />
                </div>
              </div>
            </section>
          </main>

          <Footer />

          <Scrollbar />
        </div>
      </div>
    </Fragment>
  );
};

export default EngineeringTeamExtension;